'use client'

import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react'
import { IBasicUser, INewQuoteForm, useUserApi } from '@repo/sdk'
import { Avatar, AvatarFallback, AvatarImage } from '@repo/ui'
import { cn } from '@repo/ui/lib'
import { useQuery } from '@tanstack/react-query'
import { ChevronDownIcon } from 'lucide-react'
import { useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

interface Props {
  className?: string
  onChange: (...event: any[]) => void
  value?: number
}

export const UserInput = ({ className, onChange, value }: Props) => {
  const { control } = useFormContext<INewQuoteForm>()
  const companyId = useWatch({ control, name: 'company_id' })
  const { getUser, getUsers } = useUserApi()

  const [query, setQuery] = useState('')

  const params = {
    'filter[company_id]': companyId,
    search: query,
  }

  const { data, isPending } = useQuery({
    queryFn: async () => getUsers(params),
    queryKey: ['/users', params],
  })

  const { data: user } = useQuery({
    queryFn: async () => {
      if (!value) throw new Error('Value is not defined')

      return getUser(value)
    },
    queryKey: ['/users', value],
  })

  const handleOnChange = (value: { id: number; title: string }) => {
    onChange(value.id)
  }

  return (
    <Combobox
      as={'div'}
      immediate
      // @ts-ignore`
      onChange={handleOnChange}
      onClose={() => setQuery('')}
      value={user?.data}
    >
      <div className={'relative'}>
        <ComboboxInput
          autoComplete={'off'}
          className={cn(
            'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
          // @ts-ignore
          displayValue={user =>
            // @ts-ignore
            user && `${user?.name} ${user?.last_name !== null ? user.last_name : ''}`
          }
          onChange={e => setQuery(e.target.value)}
          placeholder="User"
        />
        <ComboboxButton className="group absolute inset-y-0 right-0 px-2.5">
          <ChevronDownIcon className="size-4 fill-black/60 group-data-[hover]:fill-black" />
        </ComboboxButton>
      </div>
      <ComboboxOptions
        anchor={'bottom start'}
        className={'z-[1500] min-w-28 border bg-white empty:invisible'}
      >
        {data?.data &&
          (data?.data as unknown as IBasicUser[]).map(user => (
            <ComboboxOption className="p-2 data-[focus]:bg-blue-100" key={user.id} value={user}>
              <div className={'flex items-center gap-2 px-3 py-2'}>
                <Avatar>
                  <AvatarImage src={user?.avatar?.url} />
                  <AvatarFallback>
                    {user?.name?.substring(0, 1)}
                    {user?.last_name?.substring(0, 1)}
                  </AvatarFallback>
                </Avatar>
                <div className={'flex flex-col gap-2'}>
                  <p>
                    {user?.name} {user?.last_name}
                  </p>
                  <div className={'flex gap-1'}>
                    {[user?.phone && user.phone, user?.email && user.email]
                      .filter(Boolean) // Remove falsy values
                      .map((item, index, array) => (
                        <p key={index}>
                          {item}
                          {array && index < array.length - 1 && ','}{' '}
                          {/* Add comma except for the last item */}
                        </p>
                      ))}
                  </div>
                </div>
              </div>
            </ComboboxOption>
          ))}
        {!isPending &&
          query &&
          query?.length > 2 &&
          data?.data?.data &&
          data?.data?.data.length === 0 && (
            <ComboboxOption className={'p-2'} value="no-results">
              No results found
            </ComboboxOption>
          )}
      </ComboboxOptions>
    </Combobox>
  )
}
